import React from 'react';

const SectionTitle = ({ children }) => (
  <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-3">{children}</h2>
);

const Para = ({ children }) => (
  <p className="text-slate-700 leading-relaxed mb-3">{children}</p>
);

export default function TermsOfService() {
  const lastUpdated = 'February 2026';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="terms-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <h1 className="text-3xl font-bold text-slate-900" data-testid="terms-heading">
            Terms &amp; Conditions
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Ezy Mobility Solutions Motorcycle Rental LLC — Last updated {lastUpdated}
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <Para>
          These Terms &amp; Conditions (“Terms”) govern your access to and use of the fleet
          management platform provided by{' '}
          <strong>Ezy Mobility Solutions Motorcycle Rental LLC</strong> (“Ezy Mobility”, “we”,
          “our”, “us”) at{' '}
          <a href="https://ezygo.ae" className="text-emerald-700 underline">
            https://ezygo.ae
          </a>
          , together with all related services, including notifications delivered via our
          official WhatsApp Business account. By accessing our services or receiving our WhatsApp
          messages, you agree to be bound by these Terms.
        </Para>

        <SectionTitle>1. Acceptance of Terms</SectionTitle>
        <Para>
          By using our platform, receiving our WhatsApp messages, entering into a rental,
          employment, or service arrangement with us, or otherwise interacting with our services,
          you confirm that you have read, understood, and agree to be bound by these Terms and by
          our{' '}
          <a href="https://ezygo.ae/privacy" className="text-emerald-700 underline">
            Privacy Policy
          </a>
          . If you do not agree with any part of these Terms, please discontinue use of our
          services.
        </Para>

        <SectionTitle>2. Eligibility</SectionTitle>
        <Para>
          Our services are intended for individuals who are at least 18 years of age and legally
          capable of entering into binding contracts under the laws of the United Arab Emirates.
          By using our services, you represent that you meet these requirements and that all
          information you provide to us is true, accurate, and complete.
        </Para>

        <SectionTitle>3. Services Provided</SectionTitle>
        <Para>Ezy Mobility provides an integrated fleet-management platform that supports:</Para>
        <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-3">
          <li>Motorcycle rental, handover, and return operations.</li>
          <li>Rider onboarding, employment records, and payroll processing.</li>
          <li>
            Delivery-platform integrations (e.g. Talabat, Noon, Careem) for engagement management.
          </li>
          <li>Maintenance ticketing, spare-parts inventory, and vehicle service history.</li>
          <li>Fines, tolls (Salik), fuel usage, and reimbursement tracking.</li>
          <li>Financial ledgers including salary advances, loan accounts, and petty cash.</li>
          <li>
            Transactional notifications delivered via WhatsApp, email, and in-app channels.
          </li>
        </ul>

        <SectionTitle>4. WhatsApp Communications</SectionTitle>
        <Para>
          We use the WhatsApp Business Cloud API operated by Meta Platforms, Inc. to send
          transactional and utility messages. By providing your mobile number to us, you consent
          to receive such messages, which may include:
        </Para>
        <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-3">
          <li>Fine issuance notifications and copies of fine slips.</li>
          <li>Salary slips, payroll disbursement confirmations, and arrears alerts.</li>
          <li>Warnings, disciplinary notices, and formal policy reminders.</li>
          <li>Loan account statements and salary advance confirmations.</li>
          <li>Handover, maintenance, and vehicle-return acknowledgements.</li>
        </ul>
        <Para>
          All templates we use are pre-approved by Meta and are strictly transactional. We do not
          send marketing content over WhatsApp. You may opt out of WhatsApp messaging at any
          time by replying <strong>STOP</strong> to any message we send you, or by contacting us
          at the email address listed below. Standard messaging rates from your mobile carrier
          may apply.
        </Para>

        <SectionTitle>5. Account Security &amp; User Responsibilities</SectionTitle>
        <Para>You are responsible for:</Para>
        <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-3">
          <li>Maintaining the confidentiality of your login credentials and access tokens.</li>
          <li>
            Ensuring that any information you submit (personal, banking, or vehicle-related) is
            accurate and up to date.
          </li>
          <li>All activities that occur under your account.</li>
          <li>Notifying us immediately of any unauthorised access or breach of security.</li>
        </ul>
        <Para>
          We reserve the right to suspend or terminate access to any account that we reasonably
          believe is being used in violation of these Terms, applicable law, or our internal
          policies.
        </Para>

        <SectionTitle>6. Acceptable Use</SectionTitle>
        <Para>You agree not to:</Para>
        <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-3">
          <li>Use our services for any unlawful, fraudulent, or unauthorised purpose.</li>
          <li>Impersonate any person or misrepresent your affiliation with any entity.</li>
          <li>
            Attempt to gain unauthorised access to our systems, databases, or WhatsApp integration
            credentials.
          </li>
          <li>Interfere with, disrupt, or reverse-engineer any part of our platform.</li>
          <li>Use our services to send spam, harassment, or unlawful content.</li>
        </ul>

        <SectionTitle>7. Rental &amp; Employment Obligations</SectionTitle>
        <Para>
          Where you enter into a rental, employment, or service agreement with Ezy Mobility, that
          agreement’s specific terms (including fees, deposits, working hours, deductions,
          liabilities, and vehicle-usage rules) will apply alongside these Terms. In the event of
          a conflict, the specific agreement will prevail over these Terms only to the extent of
          the conflict.
        </Para>

        <SectionTitle>8. Intellectual Property</SectionTitle>
        <Para>
          The platform, including its design, source code, database schemas, workflows, and all
          content generated by us, is the exclusive property of Ezy Mobility Solutions
          Motorcycle Rental LLC. You may not copy, modify, distribute, or exploit any part of the
          platform without our prior written consent.
        </Para>

        <SectionTitle>9. Third-Party Services</SectionTitle>
        <Para>
          Our platform integrates with third-party services (such as WhatsApp/Meta, delivery
          platforms, payment gateways, and government portals). Your use of those services is
          subject to their respective terms and policies. We are not responsible for the acts,
          omissions, availability, or content of any third-party service.
        </Para>

        <SectionTitle>10. Limitation of Liability</SectionTitle>
        <Para>
          To the maximum extent permitted by law, Ezy Mobility shall not be liable for any
          indirect, incidental, consequential, or punitive damages arising out of or related to
          your use of our services. Our aggregate liability for any claim arising out of these
          Terms shall not exceed the fees you have paid to us in the twelve (12) months
          preceding the event giving rise to the claim.
        </Para>

        <SectionTitle>11. Termination</SectionTitle>
        <Para>
          We may suspend or terminate your access to our services at any time, with or without
          notice, if we reasonably believe you have breached these Terms, violated any applicable
          law, or engaged in conduct that could harm us, our other users, or the general public.
          Termination will not affect any obligations that by their nature should survive
          termination (including intellectual-property rights, indemnities, and limitations of
          liability).
        </Para>

        <SectionTitle>12. Changes to these Terms</SectionTitle>
        <Para>
          We may update these Terms from time to time to reflect changes in our services, legal
          obligations, or industry best practice. The latest version will always be available at{' '}
          <a href="https://ezygo.ae/terms" className="text-emerald-700 underline">
            https://ezygo.ae/terms
          </a>
          . Continued use of our services after any update constitutes your acceptance of the
          revised Terms.
        </Para>

        <SectionTitle>13. Governing Law &amp; Jurisdiction</SectionTitle>
        <Para>
          These Terms are governed by, and construed in accordance with, the laws of the Emirate
          of Dubai and the applicable federal laws of the United Arab Emirates. Any dispute
          arising out of or in connection with these Terms shall be subject to the exclusive
          jurisdiction of the courts of Dubai, UAE.
        </Para>

        <SectionTitle>14. Contact Us</SectionTitle>
        <Para>
          For any questions, complaints, or requests regarding these Terms, please contact:
        </Para>
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-8">
          <p className="text-slate-800">
            <strong>Ezy Mobility Solutions Motorcycle Rental LLC</strong>
            <br />
            Al Showaib Real Estate — Warehouse 7, Plot No 194, Amman Street,
            <br />
            Al Qusais Industrial Area 3, Dubai, United Arab Emirates
            <br />
            Email:{' '}
            <a
              href="mailto:erp@ezytronix.com"
              className="text-emerald-700 underline"
              data-testid="terms-contact-email"
            >
              erp@ezytronix.com
            </a>
            <br />
            WhatsApp: +971 50 567 0732
            <br />
            Website:{' '}
            <a href="https://ezygo.ae" className="text-emerald-700 underline">
              https://ezygo.ae
            </a>
          </p>
        </div>

        <footer className="border-t border-slate-200 pt-6 pb-12 text-xs text-slate-500">
          © {new Date().getFullYear()} Ezy Mobility Solutions Motorcycle Rental LLC. All rights
          reserved.
        </footer>
      </main>
    </div>
  );
}
